import { SEO } from '@/components/SEO';
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import LocationFAQ from "@/components/locations/LocationFAQ";
import LocationBlogLinks from "@/components/locations/LocationBlogLinks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Droplets, Sparkles, Home, Filter, Brush, Sun } from "lucide-react";
import { Link } from "react-router-dom";

const PortHuenemePage = () => {
  const navigate = useNavigate();

  const locationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ventura Power Washing - Port Hueneme",
    "description": "Professional pressure washing, window cleaning, and carpet cleaning services in Port Hueneme, CA. Serving residential and commercial properties in the Port Hueneme area.",
    "url": "https://www.venturapowerwashing.com/port-hueneme",
    "telephone": "(805) 464-7166",
    "areaServed": {
      "@type": "City",
      "name": "Port Hueneme",
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 34.1478,
      "longitude": -119.1951
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Port Hueneme",
      "addressRegion": "CA",
      "postalCode": "93041",
      "addressCountry": "US"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "07:00",
      "closes": "19:00"
    },
    "priceRange": "$$",
    "image": "https://www.venturapowerwashing.com/og-image.jpg",
    "sameAs": [
      "https://www.facebook.com/venturapowerwashing",
      "https://www.instagram.com/venturacleaningco",
      "https://www.yelp.com/biz/ventura-power-washing-thousand-oaks"
    ]
  };

  const services = [
    {
      icon: <Droplets className="h-8 w-8 text-ventura-blue" />,
      title: "Pressure Washing",
      description: "Deep cleaning for driveways, patios, decks, and exterior surfaces.",
      link: "/services/pressure-washing"
    },
    {
      icon: <Sparkles className="h-8 w-8 text-ventura-blue" />,
      title: "Window Cleaning",
      description: "Crystal-clear windows for homes and businesses.",
      link: "/services/window-washing"
    },
    {
      icon: <Home className="h-8 w-8 text-ventura-blue" />,
      title: "Carpet Cleaning",
      description: "Professional carpet and upholstery cleaning services.",
      link: "/services/carpet-cleaning"
    },
    {
      icon: <Filter className="h-8 w-8 text-ventura-blue" />,
      title: "Gutter Cleaning",
      description: "Keep gutters clear and protect your home from water damage.",
      link: "/services/gutter-cleaning"
    },
    {
      icon: <Brush className="h-8 w-8 text-ventura-blue" />,
      title: "Soft Wash",
      description: "Gentle cleaning for delicate surfaces like stucco and painted exteriors.",
      link: "/services/soft-wash"
    },
    {
      icon: <Sun className="h-8 w-8 text-ventura-blue" />,
      title: "Solar Panel Cleaning",
      description: "Maximize solar efficiency with professional panel maintenance.",
      link: "/services/solar-panel-cleaning"
    }
  ];

  const faqs = [
    {
      question: "How much does pressure washing cost in Port Hueneme, CA?",
      answer: "Most driveway, patio, and house pressure washing jobs in Port Hueneme run $150-$400, depending on square footage and surface type. We provide a free, no-obligation estimate before any work begins."
    },
    {
      question: "How much does window cleaning cost in Port Hueneme?",
      answer: "Residential window cleaning in Port Hueneme typically costs $150-$300 for a standard home, inside and out. Beachfront homes near the coast often need more frequent cleaning due to salt spray."
    },
    {
      question: "Do you offer carpet cleaning in Port Hueneme?",
      answer: "Yes. Whole-house carpet cleaning in Port Hueneme generally runs $100-$300 depending on square footage and the number of rooms. We use deep extraction methods that handle sand, salt, and coastal moisture."
    },
    {
      question: "Is solar panel cleaning worth it for Port Hueneme homes?",
      answer: "Yes — Port Hueneme's coastal salt air creates a stubborn film on solar panels that can cut efficiency by 15-30%. Professional solar panel cleaning in Port Hueneme typically costs $150-$350 and helps maximize energy production."
    }
  ];

  return (
    <>
      <SEO
        title="Pressure Washing & Cleaning Services in Port Hueneme, CA | Ventura Power Washing"
        description="Professional pressure washing, window cleaning, and carpet cleaning services in Port Hueneme, CA. Coastal cleaning experts serving 93041, 93043, and 93044 zip codes. Call (805) 464-7166!"
        keywords="pressure washing Port Hueneme, window cleaning Port Hueneme CA, carpet cleaning Port Hueneme, power washing 93041, coastal cleaning Port Hueneme"
        path="/port-hueneme"
      >
        <script type="application/ld+json">
          {JSON.stringify(locationSchema)}
        </script>
      </SEO>

      <Navbar />
      
      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-24">
          <Breadcrumbs 
            items={[
              { label: "Home", href: "/" },
              { label: "Port Hueneme", href: "/port-hueneme" }
            ]} 
          />
        </div>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-ventura-blue/10 to-ventura-green/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Professional Cleaning Services in <span className="text-ventura-blue">Port Hueneme, CA</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                Trusted pressure washing, window cleaning, and carpet cleaning services for Port Hueneme's coastal properties. Experts in salt air and marine environment cleaning. Serving 93041, 93043, and 93044 zip codes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-ventura-blue hover:bg-ventura-blue/90 text-white"
                  onClick={() => window.location.href = 'tel:+18054647166'}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call (805) 464-7166
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-ventura-blue text-ventura-blue hover:bg-ventura-blue/10"
                  onClick={() => navigate('/free-estimate')}
                >
                  Get Free Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
              Our Services in Port Hueneme
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="mb-4">{service.icon}</div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link 
                      to={service.link}
                      className="text-ventura-blue hover:text-ventura-blue/80 font-medium inline-flex items-center"
                    >
                      Learn More →
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Local Area Information */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-foreground mb-6">
                What Makes Port Hueneme Properties Unique to Clean?
              </h2>
              <p className="text-muted-foreground mb-6">
                Port Hueneme's beachfront location means salt buildup, marine moisture, and sand accumulation attack exterior
                surfaces faster than almost anywhere else in Ventura County. Our team specializes in combating these coastal
                challenges throughout the Beach Community, Hueneme Bay, Surfside, and areas near Naval Base Ventura County.
              </p>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Areas We Serve</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Beach Community</li>
                    <li>• Hueneme Bay</li>
                    <li>• Surfside</li>
                    <li>• Downtown Port Hueneme</li>
                    <li>• Lighthouse District</li>
                    <li>• Naval Base Adjacent Areas</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Coastal Cleaning Expertise</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Salt and mineral deposit removal</li>
                    <li>• Marine environment specialists</li>
                    <li>• Sand and debris cleaning</li>
                    <li>• Corrosion prevention treatments</li>
                    <li>• 100% satisfaction guarantee</li>
                    <li>• Licensed and insured</li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Button 
                  size="lg"
                  className="bg-ventura-blue hover:bg-ventura-blue/90 text-white"
                  onClick={() => navigate('/free-estimate')}
                >
                  Get Your Free Port Hueneme Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        <LocationFAQ city="Port Hueneme" faqs={faqs} />
        <LocationBlogLinks city="Port Hueneme" />
      </main>

      <Footer />
    </>
  );
};

export default PortHuenemePage;
